import { assurancesFor, WHAT_HAPPENS } from "@/frontend/constants/consent";
import { requesterLine } from "@/frontend/utils/consent-copy";
import { siteUrl } from "@/lib/docs";
import type { RequesterType } from "@/server/constants/persona";

type ConsentDisclosureProps = {
  /** The organization that invited the patient. The copy is neutral when this is unknown. */
  organizationName?: string | null;
  /**
   * The kind of requester, from `organization.requester_type`. The copy does
   * not name the kind when this value is null or absent.
   */
  requesterType?: RequesterType | null;
  /**
   * True for the individual workflow. In that workflow the reader is the
   * patient, the records are their own, and no other party asks for them.
   */
  ownRecords?: boolean;
};

/**
 * Everything the reader takes in below the heading, from who is asking down to
 * the Medicare attribution. The magic-link page and the individual signup step
 * both render this, so the assurances have one home rather than two that drift
 * apart. Each surface keeps its own heading, since they are sized differently.
 */
export function ConsentDisclosure({
  organizationName,
  requesterType,
  ownRecords = false,
}: ConsentDisclosureProps) {
  return (
    <>
      <p className="mt-2 text-body text-muted-foreground">
        {requesterLine(organizationName, requesterType, ownRecords)}
      </p>

      <h2 className="mt-6 text-caption font-semibold">How your records are shared</h2>

      <ul className="mt-3 flex flex-col gap-4">
        {assurancesFor(ownRecords).map(({ Icon, id, body }) => (
          <li key={id} className="flex items-start gap-3">
            <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-muted">
              <Icon className="size-4 text-muted-foreground" />
            </span>
            <p className="pt-1.5 text-body">{body}</p>
          </li>
        ))}
      </ul>

      <h2 className="mt-6 text-caption font-semibold">Here&rsquo;s what happens</h2>

      <p className="mt-2 text-body">{WHAT_HAPPENS}</p>
    </>
  );
}

/**
 * The agreement sentence, with both documents linked. It reads directly above
 * the action on every surface that shows the disclosure, which is why the button
 * beside it needs no checkbox.
 */
export function ConsentTermsLine() {
  return (
    <p className="text-caption text-muted-foreground">
      By continuing you agree to the{" "}
      <a
        href={siteUrl("/terms")}
        target="_blank"
        rel="noreferrer"
        className="underline underline-offset-2 hover:text-foreground"
      >
        Terms of Service
      </a>{" "}
      and{" "}
      <a
        href={siteUrl("/privacy")}
        target="_blank"
        rel="noreferrer"
        className="underline underline-offset-2 hover:text-foreground"
      >
        Privacy Policy
      </a>
    </p>
  );
}
